import React, { useState } from 'react';
import { Link } from 'react-router';
import {
  ArrowTopRightOnSquareIcon,
  ArrowTrendingUpIcon,
  ChevronRightIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/20/solid';
import DashboardLayout from '../components/DashboardLayout';
import StatusBadge from '../components/partials/StatusBadge';
import { useTradingStatus } from '../hooks/useTradingStatus';
import { useCompletedBusinesses } from '../hooks/useCompletedBusinesses';
import { useLlcMaintenanceServiceTypeId } from '../hooks/useLlcMaintenanceServiceTypeId';
import { TradingStatusDto } from '../types/trading';
import { BusinessSummaryDto } from '../types/businessSummary';
import { llcUrl } from '../constants/externalLinks';

// ─── Shared sub-components ───────────────────────────────────────────────────

function TradingIconBox({ locked = false }: { locked?: boolean }) {
  return (
    <div className="relative mb-7 inline-flex">
      <div className="dash-service-icon flex h-14 w-14 items-center justify-center rounded-xl">
        <ArrowTrendingUpIcon className="h-6 w-6" />
      </div>
      {locked && (
        <span
          className="absolute -bottom-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full"
          style={{
            background: 'var(--dash-bg-deep)',
            border: '1px solid var(--dash-border-strong)',
          }}
        >
          <LockClosedIcon
            className="h-2.5 w-2.5"
            style={{ color: 'var(--dash-text-faint)' }}
          />
        </span>
      )}
    </div>
  );
}

function StatusLabel({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="mb-3 text-[10.5px] font-semibold uppercase tracking-[0.22em]"
      style={{ color: 'var(--dash-text-faint)' }}
    >
      {children}
    </p>
  );
}

function PageHeading({ children }: { children: React.ReactNode }) {
  return (
    <h1
      className="mb-3 font-serif text-[28px] font-medium leading-snug"
      style={{ color: 'var(--dash-text)' }}
    >
      {children}
    </h1>
  );
}

function PageDescription({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="mb-7 text-[14px] leading-relaxed"
      style={{ color: 'var(--dash-text-muted)' }}
    >
      {children}
    </p>
  );
}

function LlcActiveCard({ llcName }: { llcName: string | null }) {
  return (
    <div
      className="mb-6 flex flex-col gap-2 rounded-lg px-5 py-4 md:flex-row md:items-center md:justify-between md:gap-4"
      style={{
        background: 'var(--dash-card-light)',
        border: '1px solid var(--dash-border)',
      }}
    >
      <div className="flex items-center gap-3">
        <span
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full"
          style={{ background: 'var(--dash-status-green-bg)' }}
        >
          <CheckIcon
            className="h-3.5 w-3.5"
            style={{ color: 'var(--dash-status-green)' }}
          />
        </span>
        <div>
          <p
            className="text-[14px] font-medium"
            style={{ color: 'var(--dash-text)' }}
          >
            {llcName ?? 'LLC'} active
          </p>
          <p
            className="text-[12px]"
            style={{ color: 'var(--dash-text-muted)' }}
          >
            Prerequisite met — you're ready to set up Trading
          </p>
        </div>
      </div>
      <StatusBadge
        variant="completed"
        label="Complete"
        className="self-center md:self-auto"
      />
    </div>
  );
}

type StepStatus = 'done' | 'in-review' | 'next' | 'upcoming';

interface StepRowProps {
  number: number;
  title: string;
  description: string;
  status: StepStatus;
}

function StepRow({ number, title, description, status }: StepRowProps) {
  const isDone = status === 'done';
  const isInReview = status === 'in-review';
  const isNext = status === 'next';

  return (
    <div
      className="py-3.5"
      style={{ borderBottom: '1px solid var(--dash-border)' }}
    >
      {isNext && (
        <StatusBadge
          variant="pending"
          label="Next"
          className="mb-1.5 ml-11 md:hidden"
        />
      )}
      <div className="flex gap-4">
        <div
          className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[12px] font-semibold"
          style={
            isDone
              ? {
                  background: 'var(--dash-status-green-bg)',
                  color: 'var(--dash-status-green)',
                }
              : {
                  background: 'var(--dash-border)',
                  color: 'var(--dash-text-muted)',
                }
          }
        >
          {isDone ? <CheckIcon className="h-3.5 w-3.5" /> : number}
        </div>
        <div className="min-w-0 flex-1">
          <p
            className="flex flex-wrap items-center text-[14px] font-medium"
            style={{ color: 'var(--dash-text)' }}
          >
            {title}
            {isDone && (
              <StatusBadge variant="completed" label="Done" className="ml-2" />
            )}
            {isInReview && (
              <StatusBadge
                variant="in-progress"
                label="In Review"
                className="ml-2"
              />
            )}
            {isNext && (
              <StatusBadge
                variant="pending"
                label="Next"
                className="ml-2 hidden md:inline-flex"
              />
            )}
          </p>
          <p
            className="mt-0.5 text-[13px]"
            style={{ color: 'var(--dash-text-muted)' }}
          >
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}

function BackToDashboard() {
  return (
    <Link
      to="/"
      className="inline-flex items-center gap-1.5 text-[13px] font-medium transition-colors"
      style={{ color: 'var(--dash-text-muted)' }}
      onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--dash-text)')}
      onMouseLeave={(e) =>
        (e.currentTarget.style.color = 'var(--dash-text-muted)')
      }
    >
      ← Back to dashboard
    </Link>
  );
}

function BackToPicker({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-[13px] font-medium transition-colors"
      style={{ color: 'var(--dash-text-muted)' }}
      onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--dash-text)')}
      onMouseLeave={(e) =>
        (e.currentTarget.style.color = 'var(--dash-text-muted)')
      }
    >
      ← Choose a different business
    </button>
  );
}

// ─── State: choose which business to trade under ────────────────────────────

function BusinessPickerView({
  onSelect,
}: {
  onSelect: (business: BusinessSummaryDto) => void;
}) {
  const { businesses, loading, error } = useCompletedBusinesses();

  return (
    <>
      <TradingIconBox />
      <StatusLabel>Choose a business</StatusLabel>
      <PageHeading>Which business would you like to trade under?</PageHeading>
      <PageDescription>
        Only one of your businesses can trade at a time. Pick the one you'd like
        to set up for Trading.
      </PageDescription>

      <div
        className="mb-8 overflow-hidden rounded-lg"
        style={{ border: '1px solid var(--dash-border)' }}
      >
        {loading && (
          <p
            className="px-5 py-4 text-[13px]"
            style={{ color: 'var(--dash-text-muted)' }}
          >
            Loading…
          </p>
        )}

        {!loading && error && (
          <p
            className="px-5 py-4 text-[13px]"
            style={{ color: 'var(--dash-text-muted)' }}
          >
            Could not load your businesses. Please try again later.
          </p>
        )}

        {!loading && !error && businesses.length === 0 && (
          <p
            className="px-5 py-4 text-[13px]"
            style={{ color: 'var(--dash-text-muted)' }}
          >
            You have no businesses eligible for Trading yet.
          </p>
        )}

        {!loading &&
          !error &&
          businesses.map((business, index) => (
            <button
              key={business.businessEntityId}
              type="button"
              onClick={() => onSelect(business)}
              className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:opacity-80"
              style={{
                borderTop:
                  index === 0 ? 'none' : '1px solid var(--dash-border)',
              }}
            >
              <span
                className="text-[14px] font-medium"
                style={{ color: 'var(--dash-text)' }}
              >
                {business.doingBusinessAs ?? business.legalName ?? 'LLC'}
              </span>
              <ChevronRightIcon
                className="h-3.5 w-3.5 shrink-0"
                style={{ color: 'var(--dash-text-faint)' }}
              />
            </button>
          ))}
      </div>

      <BackToDashboard />
    </>
  );
}

// ─── State: citizenship ineligible ───────────────────────────────────────────

function IneligibleCitizenshipView({ status }: { status: TradingStatusDto }) {
  return (
    <>
      <TradingIconBox locked />
      <StatusLabel>Not available for your citizenship</StatusLabel>
      <PageHeading>Trading can't be set up for this business</PageHeading>

      <div
        className="mb-6 rounded-lg px-5 py-4"
        style={{
          background: 'var(--dash-amber-bg)',
          border: '1px solid var(--dash-amber-border)',
        }}
      >
        <div className="flex gap-3">
          <ExclamationTriangleIcon
            className="mt-0.5 h-5 w-5 flex-shrink-0"
            style={{ color: 'var(--dash-amber)' }}
          />
          <div>
            <p
              className="mb-2 text-[14px] font-semibold"
              style={{ color: 'var(--dash-text)' }}
            >
              Trading isn&apos;t available for your citizenship
            </p>
            <p
              className="mb-3 text-[13px] leading-relaxed"
              style={{ color: 'var(--dash-text-muted)' }}
            >
              Trading is powered by <strong>Uphold</strong>, which currently
              only supports U.S. citizens and residents. Because the citizenship
              on file
              {status.citizenship ? ` (${status.citizenship})` : ''} is outside
              the United States, we&apos;re unable to set up Trading or share
              your information with Uphold at this time.
            </p>
            <p
              className="mb-3 text-[13px] leading-relaxed"
              style={{ color: 'var(--dash-text-muted)' }}
            >
              This is a regulatory limitation on Uphold&apos;s side — not an
              issue with your DAG account. Everything else on your dashboard
              remains fully available.
            </p>
            <p
              className="text-[13px]"
              style={{ color: 'var(--dash-text-faint)' }}
            >
              Questions?{' '}
              <a
                href="mailto:support@dag.com"
                className="underline"
                style={{ color: 'var(--dash-gold)' }}
              >
                Contact support
              </a>
              .
            </p>
          </div>
        </div>
      </div>

      {status.llcName && <LlcActiveCard llcName={status.llcName} />}

      <BackToDashboard />
    </>
  );
}

// ─── State A: No LLC ─────────────────────────────────────────────────────────

function NoLlcView() {
  return (
    <>
      <TradingIconBox locked />
      <StatusLabel>One step away</StatusLabel>
      <PageHeading>Trading unlocks once your LLC is active</PageHeading>

      <div
        className="mb-6 flex flex-col gap-2 rounded-lg px-5 py-4 md:flex-row md:items-center md:justify-between md:gap-4"
        style={{
          background: 'var(--dash-card-light)',
          border: '1px solid var(--dash-border)',
        }}
      >
        <div className="flex items-center gap-4">
          <div
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[12px] font-semibold"
            style={{
              background: 'var(--dash-border)',
              color: 'var(--dash-text-muted)',
            }}
          >
            <LockClosedIcon
              className="h-3.5 w-3.5"
              style={{ color: 'var(--dash-text-faint)' }}
            />
          </div>
          <div>
            <p
              className="text-[14px] font-medium"
              style={{ color: 'var(--dash-text)' }}
            >
              LLC setup
            </p>
            <p
              className="text-[12px]"
              style={{ color: 'var(--dash-text-muted)' }}
            >
              Required before Trading can be enabled
            </p>
          </div>
        </div>
        <StatusBadge
          variant="neutral"
          label="Not started"
          className="self-start"
        />
      </div>

      <div className="mb-8 flex flex-wrap items-center justify-center gap-4 md:justify-start">
        <a
          href={llcUrl('/dashboard')}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 rounded px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] transition-opacity hover:opacity-90"
          style={{ background: 'var(--dash-gold-fill)', color: '#111111' }}
        >
          Start LLC setup
          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 shrink-0" />
        </a>
        <BackToDashboard />
      </div>
    </>
  );
}

// ─── State B: Has LLC, no trading ────────────────────────────────────────────

function ReadyToActivateView({
  llcName,
  businessEntityId,
  onBack,
}: {
  llcName: string | null;
  businessEntityId: number;
  onBack: () => void;
}) {
  const { serviceRequestTypeId } = useLlcMaintenanceServiceTypeId();

  return (
    <>
      <TradingIconBox />
      <StatusLabel>Ready to activate</StatusLabel>
      <PageHeading>Set up Trading under your LLC</PageHeading>
      <PageDescription>
        Your LLC is active, so you're cleared to trade. Setting up takes a few
        steps: start your monthly plan, complete Uphold's business verification
        (KYB), then deposit funds. Once your entity is approved and funded,
        Trading is live.
      </PageDescription>

      <LlcActiveCard llcName={llcName} />

      <div className="mb-6">
        <StepRow
          number={1}
          title="Start your monthly plan"
          description="A monthly fee covering registered agent, maintenance, LLC compliance, LLC renewals, and Trading. We'll then hand you off to Uphold to create the account tied to your LLC."
          status="next"
        />
        <StepRow
          number={2}
          title="Complete business verification (KYB)"
          description="Uphold verifies your entity details."
          status="upcoming"
        />
        <StepRow
          number={3}
          title="Deposit funds"
          description="Fund your account to start trading under your LLC."
          status="upcoming"
        />
        <StepRow
          number={4}
          title="Start trading"
          description="Once approved and funded, Trading turns on in your sidebar automatically."
          status="upcoming"
        />
      </div>

      <div className="mb-8 flex flex-wrap items-center justify-center gap-4 md:justify-start">
        {serviceRequestTypeId ? (
          <a
            href={llcUrl(
              `/checkout?serviceRequestTypeId=${serviceRequestTypeId}&businessId=${businessEntityId}`
            )}
            className="inline-flex items-center gap-2 rounded px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] transition-opacity hover:opacity-90"
            style={{ background: 'var(--dash-gold-fill)', color: '#111111' }}
          >
            Start plan &amp; continue →
          </a>
        ) : (
          <button
            disabled
            className="inline-flex cursor-not-allowed items-center gap-2 rounded px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] opacity-50"
            style={{
              background: 'var(--dash-border)',
              color: 'var(--dash-text-muted)',
            }}
          >
            Start plan &amp; continue →
          </button>
        )}
        <BackToPicker onClick={onBack} />
      </div>
    </>
  );
}

// ─── State B2: Has LLC, state-restricted (e.g. New York) ────────────────────

function StateRestrictedView({
  llcName,
  onBack,
}: {
  llcName: string | null;
  onBack: () => void;
}) {
  return (
    <>
      <TradingIconBox />
      <StatusLabel>Not available in your state</StatusLabel>
      <PageHeading>Trading can&apos;t be set up for this business</PageHeading>

      <div
        className="mb-6 rounded-lg px-5 py-4"
        style={{
          background: 'var(--dash-amber-bg)',
          border: '1px solid var(--dash-amber-border)',
        }}
      >
        <div className="flex gap-3">
          <ExclamationTriangleIcon
            className="mt-0.5 h-5 w-5 flex-shrink-0"
            style={{ color: 'var(--dash-amber)' }}
          />
          <div>
            <p
              className="mb-2 text-[14px] font-semibold"
              style={{ color: 'var(--dash-text)' }}
            >
              Trading isn&apos;t available for New York residents
            </p>
            <p
              className="mb-3 text-[13px] leading-relaxed"
              style={{ color: 'var(--dash-text-muted)' }}
            >
              Trading is powered by <strong>Uphold</strong>, which does not
              currently accept New York residents on its platform due to state
              regulatory restrictions. Because you reside in New York,{' '}
              {llcName
                ? `we're unable to set up Trading for ${llcName}`
                : "we're unable to set up Trading"}{' '}
              or share your information with Uphold at this time.
            </p>
            <p
              className="mb-3 text-[13px] leading-relaxed"
              style={{ color: 'var(--dash-text-muted)' }}
            >
              This is a regulatory limitation on Uphold&apos;s side — not an
              issue with your DAG account. Everything else on your dashboard
              remains fully available.
            </p>
            <p
              className="text-[13px]"
              style={{ color: 'var(--dash-text-faint)' }}
            >
              Questions?{' '}
              <a
                href="mailto:support@dag.com"
                className="underline"
                style={{ color: 'var(--dash-gold)' }}
              >
                Contact support
              </a>
              .
            </p>
          </div>
        </div>
      </div>

      <LlcActiveCard llcName={llcName} />

      <div className="mb-8 flex flex-wrap items-center gap-4">
        <BackToDashboard />
        <BackToPicker onClick={onBack} />
      </div>

      <p className="text-[13px]" style={{ color: 'var(--dash-text-faint)' }}>
        Have questions before you start?{' '}
        <Link
          to="/consultations"
          className="underline"
          style={{ color: 'var(--dash-gold)' }}
        >
          Book a free consultation
        </Link>{' '}
        with a specialist.
      </p>
    </>
  );
}

// ─── State C: Has LLC, trading pending ───────────────────────────────────────

function InReviewView({ status }: { status: TradingStatusDto }) {
  return (
    <>
      <TradingIconBox />
      <StatusLabel>In review</StatusLabel>
      <PageHeading>Your Trading account is being approved</PageHeading>
      <PageDescription>
        Your plan is active and your details have been submitted for
        verification. There's nothing more to do right now — we'll email you and
        turn Trading on in your sidebar automatically once it's approved.
      </PageDescription>

      <LlcActiveCard llcName={status.llcName} />

      <div className="mb-6">
        <StepRow
          number={1}
          title="Start your monthly plan"
          description="A monthly fee covering registered agent, maintenance, LLC compliance, LLC renewals, and Trading. We'll then hand you off to Uphold to create the account tied to your LLC."
          status="done"
        />
        <StepRow
          number={2}
          title="Complete business verification (KYB)"
          description="Uphold is verifying your entity details. We'll let you know as soon as it's approved."
          status="in-review"
        />
        <StepRow
          number={3}
          title="Deposit funds"
          description="Fund your account to start trading under your LLC."
          status="upcoming"
        />
        <StepRow
          number={4}
          title="Start trading"
          description="Once approved and funded, Trading turns on in your sidebar automatically."
          status="upcoming"
        />
      </div>

      <div className="mb-8 flex flex-col items-center gap-3">
        <button
          disabled
          className="inline-flex cursor-not-allowed items-center gap-2 rounded px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] opacity-50"
          style={{
            background: 'var(--dash-border)',
            color: 'var(--dash-text-muted)',
          }}
        >
          <ClockIcon className="h-4 w-4" />
          Waiting on approval
        </button>
        <div className="flex w-full items-center justify-between">
          <BackToDashboard />
          <a
            href="/portfolio"
            className="inline-flex items-center gap-1.5 text-[13px] font-medium transition-colors"
            style={{ color: 'var(--dash-text-muted)' }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.color = 'var(--dash-text)')
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.color = 'var(--dash-text-muted)')
            }
          >
            View Trading account →
          </a>
        </div>
      </div>

      <p className="text-[13px]" style={{ color: 'var(--dash-text-faint)' }}>
        Questions about your application?{' '}
        <a
          href="mailto:support@dag.com"
          className="underline"
          style={{ color: 'var(--dash-gold)' }}
        >
          Contact support
        </a>
        .
      </p>
    </>
  );
}

// ─── State active: trading is live ───────────────────────────────────────────

function ActiveView({ status }: { status: TradingStatusDto }) {
  return (
    <>
      <TradingIconBox />
      <StatusLabel>Active</StatusLabel>
      <PageHeading>Your Trading account is live</PageHeading>
      <PageDescription>
        {status.llcName
          ? `Trading is active under ${status.llcName}.`
          : 'Your trading account is fully set up and active.'}
      </PageDescription>
      <div className="mb-8 flex flex-wrap items-center justify-center gap-4 md:justify-start">
        <a
          href="/portfolio"
          className="inline-flex items-center gap-2 rounded px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] transition-opacity hover:opacity-90"
          style={{ background: 'var(--dash-gold-fill)', color: '#111111' }}
        >
          Go to Trading →
        </a>
        <BackToDashboard />
      </div>
    </>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function TradingPage() {
  const { status, loading, error } = useTradingStatus();
  const [selectedBusiness, setSelectedBusiness] =
    useState<BusinessSummaryDto | null>(null);

  const renderContent = () => {
    if (loading) {
      return (
        <p className="text-[13px]" style={{ color: 'var(--dash-text-muted)' }}>
          Loading…
        </p>
      );
    }

    if (error || !status) {
      return (
        <p className="text-[13px]" style={{ color: 'var(--dash-text-muted)' }}>
          Could not load your trading status. Please try again later.
        </p>
      );
    }

    if (status.tradingState === 'IneligibleCitizenship')
      return <IneligibleCitizenshipView status={status} />;

    if (status.tradingState === 'Incomplete') return <NoLlcView />;

    if (status.tradingState === 'None') {
      // No business owns a trading request yet: let the user pick which one
      // to sign up, or continue with the one they already picked.
      if (selectedBusiness) {
        const llcName =
          selectedBusiness.doingBusinessAs ?? selectedBusiness.legalName;
        if (status.individualState === 'NY') {
          return (
            <StateRestrictedView
              llcName={llcName}
              onBack={() => setSelectedBusiness(null)}
            />
          );
        }
        return (
          <ReadyToActivateView
            llcName={llcName}
            businessEntityId={selectedBusiness.businessEntityId}
            onBack={() => setSelectedBusiness(null)}
          />
        );
      }
      return <BusinessPickerView onSelect={setSelectedBusiness} />;
    }

    if (status.tradingState === 'Pending')
      return <InReviewView status={status} />;
    return <ActiveView status={status} />; // Approved
  };

  return (
    <DashboardLayout>
      <div className="py-5 pl-14 pr-4 md:px-10 md:py-8">
        <nav
          className="mb-7 flex items-center gap-2 text-[12px]"
          style={{ color: 'var(--dash-text-faint)' }}
        >
          <Link
            to="/"
            style={{ color: 'var(--dash-text-faint)' }}
            className="hover:underline"
          >
            Dashboard
          </Link>
          <span>›</span>
          <span style={{ color: 'var(--dash-text-muted)' }}>DAG Trading</span>
        </nav>

        <div
          className="max-w-[560px] rounded-xl p-5 md:p-10"
          style={{
            background: 'var(--dash-card)',
            border: '1px solid var(--dash-border)',
          }}
        >
          {renderContent()}
        </div>
      </div>
    </DashboardLayout>
  );
}
